import type { AuthContextType, Tokens } from '@/providers'
import { logger, readJWTData } from '@/utils'
import { useCallback, useEffect, useRef } from 'react'

import { LogoutMessages } from '@/constants'
import { useAuth } from '@/providers'
import { useLocation } from '@tanstack/react-router'
import { useOnWindowFocusOrResume } from './useOnWindowFocusOrResume'
import { useTabUnfocus } from './useTabUnfocus'
import { useTranslation } from 'react-i18next'

const log = logger.child({
  file: 'src/hooks/useCheckJWTIsValid.tsx',
})

export type UseCheckJwtIsValidProps = {
  refreshFunction: (tokens: Tokens['refresh_token']) => Promise<void>
  apiLogoutFunction: () => Promise<void>
}

/**
 * useCheckJwtIsValid
 *
 * React hook that manages and monitors JWT (access token) validity for an authenticated session.
 * It automatically sets up refresh and logout timers, handles background tab behavior, and
 * ensures secure session expiration.
 *
 * What it does:
 * - Validates the access token on mount and when it changes.
 * - Schedules automatic refresh before expiry (2 mins before by default).
 * - Logs out the user if the token is expired or invalid.
 * - Skips checks when the tab is hidden (to avoid background resource usage).
 * - Re-checks token on window/tab focus and refreshes token on tab unfocus.
 *
 *  Lifecycle
 * - Uses `useOnWindowFocusOrResume()` to trigger a check when the user returns.
 * - Uses `useTabUnfocus()` to refresh the token when user switches tabs.
 *
 
 * @param {Object} props
 * @param {(refreshToken: Tokens['refresh_token']) => Promise<void>} props.refreshFunction
 *        Function to call for refreshing tokens. Called before token expiry.
 * @param {() => Promise<void>} props.apiLogoutFunction
 *        Optional function to call server-side logout endpoint on logout.
 *
 * @returns {void}
 *
 * @example
 * useCheckJwtIsValid({
 *   refreshFunction: myRefreshTokenFn,
 *   apiLogoutFunction: myLogoutFn
 * })
 */

export const useCheckJwtIsValid = ({
  refreshFunction,
  apiLogoutFunction,
}: UseCheckJwtIsValidProps) => {
  const { access_token, refresh_token, logout: appLogout } = useAuth()
  const location = useLocation()
  const { t } = useTranslation()

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const refreshRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isRefreshingRef = useRef(false)
  const isCheckingJWTRef = useRef(false)

  const clearTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  const clearRefreshTimer = useCallback(() => {
    if (refreshRef.current) {
      clearTimeout(refreshRef.current)
      refreshRef.current = null
    }
  }, [])

  const logout: AuthContextType['logout'] = useCallback(
    async (options) => {
      clearTimer()
      clearRefreshTimer()
      await appLogout({
        apiLogoutFunction,
        ...options,
      })
    },
    [clearTimer, clearRefreshTimer, appLogout, apiLogoutFunction],
  )

  /**
   * Function to log out the user on JWT issue or expiry
   */
  const logoutWithRedirect = useCallback(() => {
    logout({
      redirect: location,
      loggedOutMessage: t(LogoutMessages.EXPIRED),
    })
  }, [logout, location, t])

  const refresh = useCallback(
    async ({ refresh_token: refreshTkn }: { refresh_token: string }) => {
      if (isRefreshingRef.current) {
        log.info('Refresh already in progress, skipping...')
        return
      }
      isRefreshingRef.current = true
      clearRefreshTimer()
      log.info('refreshing token')

      try {
        await refreshFunction(refreshTkn)
        clearTimer() // Reset logout timer on successful refresh
      } catch (err) {
        log.error({ msg: 'Token refresh failed', error: err })
        logout({ errorMsg: t(LogoutMessages.ISSUE) })
      } finally {
        isRefreshingRef.current = false
      }
    },
    [clearRefreshTimer, clearTimer, refreshFunction, logout, t],
  )

  const AccessTokenCheck = useCallback(() => {
    if (isCheckingJWTRef.current) {
      log.info('JWT check already in progress, skipping...')
      return
    }

    if (document.hidden) {
      log.info('Document is hidden - skipping JWT check')
      clearTimer()
      clearRefreshTimer()
      return
    }

    isCheckingJWTRef.current = true
    log.info('Checking AccessToken')
    clearTimer()
    clearRefreshTimer()

    if (access_token) {
      try {
        const { exp } = readJWTData<{ exp: number }>(access_token)
        if (!exp || isNaN(exp)) {
          throw new Error('Invalid token expiration')
        }

        const expireTime = exp * 1000
        const now = Date.now()

        if (expireTime > now) {
          const diff = expireTime - now
          const timeBeforeTokenExp = 1000 * 60 * 2 // 2 minutes before expiry
          const refreshIn = Math.max(diff - timeBeforeTokenExp, 0)

          log.debug(
            `AccessToken valid. Expires in ${diff}ms. Refresh in ${refreshIn}ms.`,
          )

          if (refresh_token) {
            refreshRef.current = setTimeout(() => {
              refresh({ refresh_token })
            }, refreshIn)
          }

          timeoutRef.current = setTimeout(() => {
            log.debug('Token expired - logging out')
            logoutWithRedirect()
          }, diff)
        } else {
          log.debug('Token already expired - logging out')
          logoutWithRedirect()
        }
      } catch (e) {
        log.error({
          msg: 'Invalid AccessToken',
          error: e,
          access_token,
          refresh_token,
        })
        logout({ errorMsg: t(LogoutMessages.ISSUE) })
      }
    }

    isCheckingJWTRef.current = false
  }, [
    access_token,
    refresh_token,
    clearTimer,
    clearRefreshTimer,
    refresh,
    logoutWithRedirect,
    logout,
    t,
  ])

  // Initial check on access_token change
  useEffect(() => {
    if (!access_token) {
      return
    }

    log.debug('Access token check')
    AccessTokenCheck()

    return () => {
      clearTimer()
      clearRefreshTimer()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [access_token])

  // Check again when tab regains focus
  useOnWindowFocusOrResume(() => {
    log.debug('Window focus or resume - checking access token')
    AccessTokenCheck()
  })

  // Refresh proactively when the tab becomes unfocused
  useTabUnfocus(() => {
    if (access_token && refresh_token) {
      log.debug('Tab unfocus - triggering refresh')
      clearTimer()
      clearRefreshTimer()
      refresh({ refresh_token })
    }
  })
}
